import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Dialog,
  Button,
  MenuItem,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import useProductOptions from 'src/hooks/use-product-options';
import axios, { endpoints } from 'src/utils/axios';
import { ProductType } from 'src/type/product';
import { PriceUnit } from 'src/type/position';

const defaultValues = {
  productId: '',
  customPriceBuyPerUnit: 0,
  customPriceSellPerUnit: 0,
};

const Schema = Yup.object().shape({
  productId: Yup.string().required('กรุณาเลือกสินค้า'),
  customPriceBuyPerUnit: Yup.number()
    .typeError('ราคารับซื้อต้องเป็นตัวเลข')
    .required('จำเป็นต้องระบุราคารับซื้อ')
    .moreThan(0, 'ราคารับซื้อต้องมากกว่า 0'),
  customPriceSellPerUnit: Yup.number()
    .typeError('ราคาขายต้องเป็นตัวเลข')
    .required('จำเป็นต้องระบุราคาขาย')
    .moreThan(0, 'ราคาขายต้องมากกว่า 0'),
});

type Props = {
  open: boolean;
  onClose: VoidFunction;
  handleReload: VoidFunction;
  products: ProductType[];
};

export default function CreateCustomPriceDialogDialog({
  open,
  onClose,
  handleReload,
  products,
}: Props) {
  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = methods;

  const { productOptions } = useProductOptions(true);

  const selectedProductId = watch('productId');
  const selectedProduct = productOptions.find((opt) => opt.value === selectedProductId)?.info;
  const isSilverProduct = selectedProduct ? /เงิน|silver/i.test(selectedProduct.name || '') : false;
  const priceUnit = isSilverProduct ? PriceUnit.PRICE_PER_KG : PriceUnit.PRICE_PER_BAHT;

  const priceUnitLabel = isSilverProduct ? 'ต่อกิโลกรัม' : 'ต่อบาท';
  const priceUnitSuffix = isSilverProduct ? 'THB/กก.' : 'THB';

  const onSubmit = handleSubmit(async (values) => {
    try {
      const body = {
        productId: values.productId,
        customPriceBuyPerUnit: Number(values.customPriceBuyPerUnit),
        customPriceSellPerUnit: Number(values.customPriceSellPerUnit),
        priceUnit,
      };

      await axios.post(endpoints.product.priceLog, body);

      handleReload();
      enqueueSnackbar('สร้างราคา Custom สำเร็จ', { variant: 'success' });

      beforeClose();
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>สร้างราคา Custom</DialogTitle>

        <DialogContent>
          <Stack spacing={3} py={1}>
            <RHFSelect name="productId" label="สินค้า" placeholder="เลือกสินค้า">
              {productOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFTextField
              name="customPriceSellPerUnit"
              type="number"
              label={`ราคาขาย (${priceUnitLabel})`}
              placeholder="0"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography fontWeight="bold" color="success.main">
                      {priceUnitSuffix}
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />

            <RHFTextField
              name="customPriceBuyPerUnit"
              type="number"
              label={`ราคารับซื้อ (${priceUnitLabel})`}
              placeholder="0"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography fontWeight="bold" color="success.main">
                      {priceUnitSuffix}
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
